import { Trash2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { OrderProductImportButton } from "@/modules/order-products";

const inputClass = "w-full h-7 px-1.5 font-mono text-xs bg-transparent focus:outline-none focus:ring-1 focus:ring-brand rounded-sm";

export const PriceListRow = ({
  row, idx, columns,
  onFieldChange, onSaveRow, onRemoveRow, fmtTime,
  itemNumber, onImportOrderCarton,
}) => {
  const { t } = useLanguage();

  // factory cost is entered in one currency; toggle switches which field the input edits
  const costCur = row.cost_currency === "USD" || (!row.cost_currency && (row.factory_cost_usd || 0) > 0) ? "USD" : "RMB";
  const costField = costCur === "USD" ? "factory_cost_usd" : "factory_cost";

  const toggleCostCurrency = () => {
    const next = costCur === "USD" ? "RMB" : "USD";
    onFieldChange(idx, "cost_currency", next);
    if (row.id) onSaveRow(idx, { ...row, cost_currency: next });
  };

  return (
    <tr data-testid={`price-list-row-${idx}`} className="border-b border-border last:border-0">
      {columns.map(([key, , , editable, type]) => {
        if (type === "cost") {
          return (
            <td key={key} className="border-r border-border p-0.5">
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={toggleCostCurrency}
                  title={t("priceList.toggleCostCurrency")}
                  data-testid={`price-list-cost-currency-${idx}`}
                  className="shrink-0 font-mono text-[10px] font-medium text-muted-foreground hover:text-brand transition-colors"
                >
                  {costCur === "USD" ? "US$" : "¥"}
                </button>
                <input
                  type="number"
                  step="0.001"
                  value={row[costField] ?? ""}
                  onChange={(e) => onFieldChange(idx, costField, e.target.value)}
                  onBlur={() => onSaveRow(idx)}
                  data-testid={`price-list-factory_cost-input-${idx}`}
                  className={`${inputClass} font-bold text-right`}
                />
              </div>
            </td>
          );
        }
        return editable ? (
          <td key={key} className="border-r border-border p-0.5">
            <input
              type={type}
              step={type === "number" ? "0.001" : undefined}
              value={row[key] ?? ""}
              onChange={(e) => onFieldChange(idx, key, e.target.value)}
              onBlur={() => onSaveRow(idx)}
              data-testid={`price-list-${key}-input-${idx}`}
              className={inputClass}
            />
            {key === "size" && (row.source_buyer || row.imported_at) && (
              <div className="px-1.5 pb-0.5 font-mono text-[10px] leading-tight text-muted-foreground truncate"
                title={`${t("priceList.importedFrom")}${row.source_buyer || "—"}${row.imported_at ? ` · ${row.imported_at}` : ""}`}
                data-testid={`price-list-source-note-${idx}`}>
                {[row.source_buyer, row.imported_at].filter(Boolean).join(" · ")}
              </div>
            )}
          </td>
        ) : (
          <td key={key} className="border-r border-border px-1.5 font-mono text-xs font-semibold text-brand whitespace-nowrap overflow-hidden" data-testid={`price-list-${key}-${idx}`}>
            {key === "container_qty_40_compressed"
              ? (Math.round(row.container_qty_40_compressed || row.container_qty_40 || 0) || "-")
              : (Number(row[key]) || 0).toFixed(key === "display_price_usd" || key === "display_price_cad" ? 2 : 3)}
          </td>
        );
      })}
      <td className="border-r border-border px-1.5 font-mono text-[10px] text-muted-foreground whitespace-nowrap overflow-hidden" data-testid={`price-list-quote-time-${idx}`}>
        {fmtTime(row.quote_time)}
      </td>
      <td className="p-0.5">
        <div className="flex items-center">
          {onImportOrderCarton && (
            <OrderProductImportButton
              itemNumber={itemNumber || ""}
              iconOnly
              variant="ghost"
              allowPriceImport
              currentPrice={Number(row.factory_cost || 0)}
              testId={`price-list-import-order-${idx}`}
              onPick={(rec, opts) => onImportOrderCarton(idx, rec, opts)}
            />
          )}
          <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => onRemoveRow(idx)} data-testid={`remove-price-list-row-${idx}`}>
            <Trash2 size={13} className="text-destructive" />
          </Button>
        </div>
      </td>
    </tr>
  );
};
